import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isElectron } from '../../api';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { getRemoteClientStore } from '../../api/stores/remoteClientStores';
import { getMediaTrackFromIdAsPromise, refreshDisplayShadowcamStreams } from '../../api/modules/managers/mediaManager';
import globalFrame from '../../../assets/global-frame.png';
import config from '../../config.json';
import { mediaCoordinator, sessionCoordinator } from '../../coordinators/init';
import { USE_MEDIA_COORDINATOR, USE_SESSION_COORDINATOR } from '../../config/featureFlags';

const GREEN_DOMINANCE_THRESHOLD = 0.65;
const PREVIEW_HEALTH_CHECK_MS = 3_000;
const PREVIEW_RECOVERY_COOLDOWN_MS = 10_000;

function isGreenGarbageFrame(imageData: ImageData): boolean {
  const { data, width, height } = imageData;
  let greenDominant = 0;
  let count = 0;
  for (let y = 0; y < height; y += 4) {
    for (let x = 0; x < width; x += 4) {
      const i = (y * width + x) * 4;
      const b = data[i];
      const g = data[i + 1];
      const r = data[i + 2];
      if (g > 160 && g > r + 35 && g > b + 35) {
        greenDominant += 1;
      }
      count += 1;
    }
  }
  return count > 0 && greenDominant / count >= GREEN_DOMINANCE_THRESHOLD;
}

export const Preview = () => {
  const context = useContext(PlaycastContext);
  const remoteStores = getRemoteClientStore();
  const previewRef = useRef<HTMLVideoElement>(null);
  const [showStaticImage, setShowStaticImage] = useState(false);
  const [staticImageContent, setStaticImageContent] = useState('');
  const [shouldShowPreview, setShouldShowPreview] = useState(
    () => context.stateStores.hostRuleState.showVideoPreview ?? true,
  );
  const [previewShadowcam, setPreviewShadowcam] = useState(context.stateStores.hostRuleState.videoPreviewCamera);
  const [isWindowMinimized, setIsWindowMinimized] = useState(false);
  const currentSourceRef = useRef('');
  const lastPreviewRecoveryRef = useRef(0);
  const previewHealthCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const attachPreviewTrack = useCallback((mediaTrack: MediaStreamTrack) => {
    if (!previewRef.current) {
      return;
    }
    const currentStream = previewRef.current.srcObject as MediaStream | null;
    const currentTrack = currentStream?.getVideoTracks()[0];
    if (mediaTrack.id === currentSourceRef.current || currentTrack?.id === mediaTrack.id) {
      currentSourceRef.current = mediaTrack.id;
      return;
    }
    console.debug('[Preview] Attaching video track', mediaTrack.label, mediaTrack.id);
    const mediaStream = new MediaStream();
    mediaStream.addTrack(mediaTrack);
    previewRef.current.srcObject = mediaStream;
    currentSourceRef.current = mediaTrack.id;
    setShowStaticImage(false);
    void previewRef.current.play().catch((err) => {
      console.warn('[Preview] play() failed:', err);
    });
  }, []);

  const recoverPreviewCapture = useCallback(async () => {
    const now = Date.now();
    if (now - lastPreviewRecoveryRef.current < PREVIEW_RECOVERY_COOLDOWN_MS) {
      return;
    }

    const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
    if (USE_MEDIA_COORDINATOR && isElectron) {
      if (USE_SESSION_COORDINATOR && sessionCoordinator.getStatus() === 'hosting') {
        console.debug('[Preview] Skipping green-frame recovery — broadcast is active');
        return;
      }
      lastPreviewRecoveryRef.current = now;
      console.warn('[Preview] Detected green garbage frame — restarting shadowcam capture');
      await mediaCoordinator.restartShadowcamCapture(previewCam);
      const track = mediaCoordinator.getVideoTrack();
      if (track && track.label.includes(previewCam)) {
        currentSourceRef.current = '';
        attachPreviewTrack(track);
      }
      return;
    }

    lastPreviewRecoveryRef.current = now;
    console.warn('[Preview] Detected green garbage frame — restarting shadowcam capture');
    void refreshDisplayShadowcamStreams();
  }, [attachPreviewTrack, previewShadowcam]);

  const setPreviewRef = useCallback(async () => {
    if (isElectron && context.stateStores.videoState.selectedDevice) {
      if (context.stateStores.videoState.selectedDevice === '0') {
        setShowStaticImage(true);
        return;
      }
      setShowStaticImage(false);

      if (USE_MEDIA_COORDINATOR) {
        const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
        const liveTrack = mediaCoordinator.getVideoTrack();
        if (liveTrack?.readyState === 'live' && liveTrack.label.includes(previewCam)) {
          attachPreviewTrack(liveTrack);
          return;
        }
        await mediaCoordinator.startShadowcamCapture(previewCam);
        const coordinatorTrack = mediaCoordinator.getVideoTrack();
        if (coordinatorTrack && coordinatorTrack.label.includes(previewCam)) {
          attachPreviewTrack(coordinatorTrack);
        }
        return;
      }

      const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
      let selectedDevice = context.stateStores.videoState.selectedDevice;
      if (selectedDevice === 'display') {
        selectedDevice = `display-${previewCam}`;
      }
      if (selectedDevice === 'followMouse') {
        selectedDevice = `followMouse-${previewCam}`;
      }
      const mediaTrack = await getMediaTrackFromIdAsPromise(selectedDevice);
      if (mediaTrack) {
        attachPreviewTrack(mediaTrack);
      }
    } else {
      if (context.stateStores.videoState.selectedDevice === '0') {
        setShowStaticImage(true);
      } else {
        setShowStaticImage(false);
        const mediaStream = remoteStores.previewStreamState.currentState;
        if (mediaStream) {
          if (previewRef.current) {
            previewRef.current.srcObject = mediaStream;
            previewRef.current.load();
          }
        }
      }
    }
    // if not already playing, start playing
    if (previewRef.current && previewRef.current.paused) {
      if (previewRef.current.srcObject) {
        // await previewRef.current.play();
      }
    }
  }, [context.stateStores.videoState.selectedDevice, previewShadowcam, remoteStores.previewStreamState.currentState, attachPreviewTrack]);

  useEffect(() => {
    if (!USE_MEDIA_COORDINATOR || !isElectron) return;
    const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
    return mediaCoordinator.events.tracksChanged.addListener(({ videoTrack }) => {
      if (videoTrack?.readyState === 'live' && videoTrack.label.includes(previewCam)) {
        attachPreviewTrack(videoTrack);
      }
    });
  }, [attachPreviewTrack, previewShadowcam]);

  useEffect(() => {
    setStaticImageContent(context.stateStores.staticImageState.staticImageBase64);
  }, [context.stateStores.staticImageState.staticImageBase64]);

  useEffect(() => {
    const listener = remoteStores.previewTrackState.addListener(() => {
      setPreviewRef();
    });
    setStaticImageContent(context.stateStores.staticImageState.staticImageBase64);
    return () => {
      remoteStores.previewTrackState.removeListener(listener);
    };
  }, [setPreviewRef]);

  useEffect(() => {
    setShouldShowPreview(context.stateStores.hostRuleState.showVideoPreview);
    setPreviewShadowcam(context.stateStores.hostRuleState.videoPreviewCamera);
  }, [context.stateStores.hostRuleState]);

  const selectedDevice = context.stateStores.videoState.selectedDevice;
  const shadowcamCount = context.stateStores.videoState.shadowcams.length;

  useEffect(() => {
    setPreviewRef();
  }, [shouldShowPreview, previewShadowcam, selectedDevice, setPreviewRef]);

  useEffect(() => {
    if (!isElectron || USE_MEDIA_COORDINATOR) return;
    void refreshDisplayShadowcamStreams();
  }, [shadowcamCount]);

  useEffect(() => {
    const listener = remoteStores.previewStreamState.addListener(() => {
      setPreviewRef();
    });
    return () => {
      remoteStores.previewStreamState.removeListener(listener);
    };
  }, [setPreviewRef]);

  // PERFORMANCE: Pause video preview when window is minimized
  // This eliminates 80-90% of idle CPU usage when minimized
  useEffect(() => {
    if (!isElectron) return;
    
    const handleWindowState = (state: string) => {
      if (state === 'minimized') {
        console.log('Window minimized - pausing preview video');
        setIsWindowMinimized(true);
        if (previewRef.current) {
          previewRef.current.pause();
        }
      } else if (state === 'normal' || state === 'maximized') {
        console.log('Window restored - resuming preview video');
        setIsWindowMinimized(false);
        if (previewRef.current && previewRef.current.srcObject) {
          previewRef.current.play().catch((err) => {
            console.warn('Failed to resume video:', err);
          });
        }
      }
    };

    // Listen for window state changes from Electron
    window.electron?.on('window-state-changed', handleWindowState);

    return () => {
      window.electron?.removeListener?.('window-state-changed', handleWindowState);
    };
  }, []);

  // Also pause/resume based on isWindowMinimized state
  useEffect(() => {
    if (previewRef.current) {
      if (isWindowMinimized) {
        previewRef.current.pause();
      } else if (previewRef.current.srcObject) {
        previewRef.current.play().catch((err) => {
          console.warn('Failed to play video:', err);
        });
      }
    }
  }, [isWindowMinimized]);

  useEffect(() => {
    if (!isElectron || !shouldShowPreview || showStaticImage || isWindowMinimized) {
      return;
    }

    const timer = window.setInterval(() => {
      const video = previewRef.current;
      if (!video || video.videoWidth === 0 || video.videoHeight === 0 || video.paused) {
        return;
      }

      if (!previewHealthCanvasRef.current) {
        previewHealthCanvasRef.current = document.createElement('canvas');
      }
      const canvas = previewHealthCanvasRef.current;
      const sampleWidth = 64;
      const sampleHeight = 36;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        return;
      }

      ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);
      const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
      if (isGreenGarbageFrame(imageData)) {
        void recoverPreviewCapture();
      }
    }, PREVIEW_HEALTH_CHECK_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [isWindowMinimized, recoverPreviewCapture, shouldShowPreview, showStaticImage]);

  return (
    shouldShowPreview &&
    (showStaticImage ? (
      <img
        id='preview-static-image'
        alt='static preview'
        className='aspect-video size-full max-h-full max-w-full bg-black object-cover'
        src={staticImageContent ? staticImageContent : globalFrame}
      />
    ) : (
      <video
        className='size-full bg-black object-contain'
        ref={previewRef}
        autoPlay
        muted
        playsInline
      />
    ))
  );
};
